// Copyright (c) Meta Platforms, Inc. and affiliates.

/**
 * @file FieldStatus.tsx
 * @input Uses React, stylex, theme tokens, useAnnounce, Icon
 * @output Exports FieldStatus component, FieldStatusProps
 * @position Core implementation; consumed by Field, Switch, CheckboxInput, and the FieldStatus entrypoint
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/FieldStatus/FieldStatus.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Field/Field.doc.mjs (compat docs when public API changes)
 * - /packages/core/src/FieldStatus/index.ts (exports if types change)
 * - /packages/core/src/Field/index.ts (compat re-export if public API changes)
 * - /packages/cli/assets/templates/blocks/components/FieldStatus/ (showcase blocks)
 */

'use client';

import React, { useEffect } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useAnnounce } from "../hooks/useAnnounce.js";
import { mergeProps } from "../utils/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, radiusVars, spacingVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { useEntryAnimation } from "../hooks/useEntryAnimation.js";
import { themeProps } from "../utils/themeProps.js";
import { Icon } from "../Icon/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Maps each status type to its status glyph. Mirrors the mapping the input
 * controls already use for the on-field status affordance, so the detached
 * message shows the same icon a consumer sees elsewhere for that status.
 */
const statusIconMap = {
  warning: 'warning',
  error: 'error',
  success: 'success'
};
const styles = {
  base: {
    kMv6JI: "x9ynric",
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    $$css: true
  },
  attached: {
    keoZOQ: "x1c40v9y",
    kLKAdn: "x1ak3mig",
    kGO01o: "x1wesfrj",
    kg3NbH: "xf314gf",
    kVL7Gh: "xquck67",
    kT0f0o: "x14i3lts",
    $$css: true
  },
  detached: {
    keoZOQ: "xcsaf9d",
    k8WAf4: "xce4md1",
    kg3NbH: "xf314gf",
    kaIpWk: "xh6dtrn",
    $$css: true
  }
};
const colorStyles = {
  warning: {
    kWkggS: "x24i8r5",
    kMwMTN: "xdhq94a",
    $$css: true
  },
  error: {
    kWkggS: "x1pritpl",
    kMwMTN: "x1joocv1",
    $$css: true
  },
  success: {
    kWkggS: "xu13z74",
    kMwMTN: "xltfdvo",
    $$css: true
  }
};

/**
 * FieldStatus variant type. Extensible via module augmentation of FieldStatusVariantMap.
 */

/**
 * A status message component for form fields.
 *
 * The `detached` variant renders a leading status icon before the message so
 * status is not conveyed by color or position alone (WCAG 1.4.1). The icon is
 * decorative for assistive tech (`aria-hidden`): the message text already names
 * the status in words and is announced through the live region. The `attached`
 * variant keeps its status affordance on the bordered input, so it renders no
 * icon here to avoid a duplicate. The `tooltip` variant renders no message box
 * at all — the input surfaces the status through a tooltip on its on-field
 * icon — so callers skip rendering FieldStatus for it.
 *
 * Screen-reader announcements go through the persistent `useAnnounce` live
 * regions (assertive for errors, polite otherwise) rather than `role`/
 * `aria-live` on the rendered element. Live regions that mount together with
 * their content are not reliably announced by assistive technology, and
 * FieldStatus is almost always conditionally rendered by its callers. The
 * message is announced whenever it appears — including on first mount — and
 * whenever it changes.
 *
 * @example
 * ```
 * <FieldStatus
 *   type="error"
 *   message="This field is required"
 * />
 * <FieldStatus
 *   type="warning"
 *   message="This will be visible to others"
 *   variant="detached"
 * />
 * ```
 */
export function FieldStatus({
  ref,
  type,
  message,
  id,
  variant = 'attached',
  xstyle,
  className,
  style,
  ...rest
}) {
  const entryStyle = useEntryAnimation('slideDown');
  const announce = useAnnounce();

  // Announce the message through the persistently-mounted live regions.
  // Announce-on-mount is intentional: callers conditionally mount FieldStatus
  // when a status appears (and whole forms can mount with a server-side
  // validation error already present), and both cases must be heard.
  useEffect(() => {
    if (message) {
      announce(message, type === 'error' ? 'assertive' : 'polite');
    }
  }, [announce, message, type]);
  return /*#__PURE__*/_jsx("div", {
    ref: ref,
    id: id,
    ...rest,
    ...mergeProps(themeProps('field-status', {
      type,
      variant
    }), stylex.props(styles.base, entryStyle, variant === 'attached' ? styles.attached : styles.detached, colorStyles[type], xstyle), className, style),
    children: variant === 'detached' ? /*#__PURE__*/_jsxs("span", {
      ...{
        className: "x78zum5 x1cy8zhl xzye2dw"
      },
      children: [/*#__PURE__*/_jsx("span", {
        ...{
          className: "x3nfvp2 x6s0dn4 x14o5nre x2lah0s"
        },
        children: /*#__PURE__*/_jsx(Icon, {
          icon: statusIconMap[type],
          size: "sm",
          color: "inherit"
          // Stable theme target on the detached message box's leading glyph
          // itself, so a theme can restyle just this icon (color, size) —
          // and each status — via `defineTheme`. Same-element rules in
          // @layer astryx-theme win over the icon's own base
          // width/height/fontSize, which a field-level target could not
          // reach.
          ,
          ...themeProps('field-status-icon', {
            type
          })
        })
      }), /*#__PURE__*/_jsx("span", {
        children: message
      })]
    }) : message
  });
}
FieldStatus.displayName = 'FieldStatus';